import {createContext,useContext,useLayoutEffect,useRef,useState,type ReactNode} from 'react';
import type {Snapshot} from '@platform/contracts/x';
import type {AwardStreamBoundary} from '@platform/client';
import {followCardRankMotion} from './cardMotion';
import {useCardMarkerEffect} from './CardMarkerEffect';
import {ballFlightFrames} from './ballFlight';

type Cue={drawId:string;eventId:string;number:number;startedAt:number};
const Context=createContext<Cue|null>(null);

// One cue for the visible draw, not a timer or subscription for every card.
// Entry, reconnection, draw changes and skipped history establish a baseline.
export function ConfirmedBallMotion({state,stream,children}:{state:Snapshot;stream:AwardStreamBoundary;children:ReactNode}){
 const last=state.balls.at(-1),previous=useRef<{drawId:string;eventId?:string;sequence:number;generation:number;ready:boolean}|undefined>(undefined);
 const [cue,setCue]=useState<Cue|null>(null);
 const ready=stream.ready&&stream.currentDrawId===state.drawId&&!document.hidden;
 useLayoutEffect(()=>{
  const before=previous.current;
  previous.current={drawId:state.drawId,eventId:last?.eventId,sequence:last?.sequence??0,generation:stream.generation,ready};
  const continuous=ready&&before?.ready&&before.drawId===state.drawId&&before.generation===stream.generation;
  if(!continuous){setCue(null);return;}
  if(last?.eventId===before.eventId)return;
  if(last&&last.sequence===before.sequence+1&&['Live','Completed'].includes(state.status)&&state.tombala.status!=='Cancelled'){
   setCue({drawId:state.drawId,eventId:last.eventId,number:last.number,startedAt:document.timeline.currentTime as number??performance.now()});
  }else setCue(null);
 },[state.drawId,state.status,state.tombala.status,last?.eventId,last?.sequence,last?.number,stream.generation,ready]);
 return <Context.Provider value={cue}>{children}</Context.Provider>;
}

// Authority updates immediately, presentation waits one rank interval for ALL
// fresh hits: ball -> card movement -> marker. Backwards fill hides the marker
// during the wait without delaying data, a React frame loop or per-cell timers.
export function useConfirmedBallAnimation<T extends HTMLElement>(drawId:string|undefined,number:number|null|undefined,kind:'ball'|'cell'){
 const cue=useContext(Context),element=useRef<T>(null);
 const {effect}=useCardMarkerEffect(),choice=kind==='cell'?effect:'ink',previousChoice=useRef(choice);
 useLayoutEffect(()=>{
  // Changing the saved design setting changes resting marks; it never replays history.
  const switched=previousChoice.current!==choice;previousChoice.current=choice;
  if(switched)return;
  const node=element.current;
  if(!node||!cue||cue.drawId!==drawId||cue.number!==number||document.hidden||matchMedia('(prefers-reduced-motion: reduce)').matches)return;
  const card=kind==='cell'?node.closest<HTMLElement>('.bingo-card'):null;
  if(card){const r=card.getBoundingClientRect();if(r.bottom<=0||r.top>=innerHeight)return;}
  const style=getComputedStyle(node),raw=(style.getPropertyValue(kind==='cell'?'--motion-card-hit':'--motion-confirmed-ball')||style.getPropertyValue('--motion-ball')).trim();
  const duration=parseFloat(raw)*(raw.endsWith('ms')?1:1000),iterations=kind==='cell'&&choice==='ink'?2:1;
  const rankRaw=style.getPropertyValue('--motion-card-rank').trim();
  const rankDuration=parseFloat(rankRaw)*(rankRaw.endsWith('ms')?1:1000);
  const delay=kind==='cell'&&Number.isFinite(rankDuration)?Math.max(0,rankDuration):0;
  const now=document.timeline.currentTime as number??performance.now();
  if(!Number.isFinite(duration)||duration<=0||now-cue.startedAt>=delay+duration*iterations)return;
  const animations:Animation[]=[];
  let start=cue.startedAt+delay;
  const animate=(target:Element,frames:Keyframe[],id:string,repeat=1)=>{const animation=target.animate(frames,{duration,iterations:repeat,fill:'backwards',easing:'linear'});animation.id=id;animation.startTime=start;animations.push(animation);};
  if(kind==='cell'){
   const numeral=node.querySelector('.card-cell-number'),marker=node.querySelector(choice==='ink'?'.card-marker path':'.card-marker');if(!numeral||!marker)return;
   const ink=style.getPropertyValue('--card-mark-ink').trim(),unmarkedInk=style.getPropertyValue('--card-number-ink').trim();
   if(choice==='ink'){
    animate(marker,[
    {strokeDashoffset:'1',opacity:0,offset:0,easing:'steps(1,end)'},
    {strokeDashoffset:'.98',opacity:1,offset:.02,easing:'cubic-bezier(.35,.05,.25,1)'},
    {strokeDashoffset:'0',opacity:1,offset:.8},
    {strokeDashoffset:'0',opacity:1,offset:1}
    ],'card-marker-stroke',iterations);
   }
   else animate(marker,[
    {transform:`translateY(${style.getPropertyValue('--card-hit-lift').trim()}) scale(${style.getPropertyValue('--card-hit-scale').trim()})`,opacity:0,filter:'brightness(1)',offset:0,easing:'ease-out'},
    {transform:'translateY(-3px) scale(1.2)',opacity:1,filter:'brightness(1)',offset:.3,easing:'ease-in'},
    {transform:'translateY(1px) scale(.9)',opacity:1,filter:'brightness(1)',offset:.55,easing:'ease-out'},
    {transform:'translateY(0) scale(1.06)',opacity:1,filter:'brightness(1.2)',offset:.73},
    {transform:'translateY(0) scale(1)',opacity:1,filter:'brightness(1)',offset:1}
   ],'card-marker-drop');
   // Ink turns the number white before drawing; only the stroke repeats.
   animate(numeral,[
    {color:unmarkedInk,offset:0,easing:choice==='ink'?'steps(1,start)':'linear'},
    {color:ink,offset:.3},
    {color:ink,offset:1}
   ],'card-marker-ink');
  }else animate(node,ballFlightFrames(node,style),'confirmed-ball');
  const unfollow=card?followCardRankMotion(card,cue.eventId,landing=>{
   if(animations.every(a=>['idle','finished'].includes(a.playState)))return;
   start=Math.max(start,landing);for(const animation of animations)animation.startTime=start;
  }):undefined;
  const stop=()=>{unfollow?.();for(const animation of animations)animation.cancel();};
  const reduced=matchMedia('(prefers-reduced-motion: reduce)');
  const onReduced=()=>{if(reduced.matches)stop();};
  const onHidden=()=>{if(document.hidden)stop();};
  reduced.addEventListener('change',onReduced);document.addEventListener('visibilitychange',onHidden);
  return()=>{stop();reduced.removeEventListener('change',onReduced);document.removeEventListener('visibilitychange',onHidden);};
 },[cue,drawId,number,kind,choice]);
 return element;
}
